import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useCountyApi } from '../../services/api/useCounty.api.ts';
import { useTeamApi } from '../../services/api/useTeam.api.ts';
import { useTeamTypeApi } from '../../services/api/useTeamType.api.ts';
import { TeamCreate } from '../../types/Team/TeamCreate.type.ts';
import DatasetsMultiSelect from './DatasetsMultiSelect.tsx';

interface TeamCreateFormProps {
  snackbarData: (message: string, severity: 'success' | 'error') => void;
}

function TeamCreateForm({ snackbarData }: TeamCreateFormProps) {
  const queryClient = useQueryClient();
  const { getCounties } = useCountyApi();
  const { createTeam } = useTeamApi();
  const { getTeamTypes } = useTeamTypeApi();

  const [teamName, setTeamName] = useState<string>('');
  const [selectedCounty, setSelectedCounty] = useState<number>();
  const [selectedTeamType, setSelectedTeamType] = useState<number>();
  const [selectedDatasets, setSelectedDatasets] = useState<number[]>([]);

  const { data: counties } = useQuery({
    queryKey: ['counties'],
    queryFn: () => getCounties({ is_active: true }),
  });

  const { data: teamTypes } = useQuery({
    queryKey: ['teamTypes'],
    queryFn: getTeamTypes,
  });

  const isTeamCreateValid = !teamName || !selectedCounty || !selectedTeamType;

  const handleCountyChange = (countyId: number) => {
    setSelectedCounty(countyId);
    // Select dataset based on team country
    setSelectedDatasets((prev) => (prev.includes(countyId) ? prev : [...prev, countyId]));
  };

  const { mutate: createTeamMutatation } = useMutation({
    mutationKey: ['createTeam'],
    mutationFn: (teamData: TeamCreate) => createTeam(teamData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allTeams'] });
      setTeamName('');
      setSelectedCounty(undefined);
      setSelectedTeamType(undefined);
      setSelectedDatasets([]);
      snackbarData('Team created successfully!', 'success');
    },
    onError: (error) => {
      snackbarData(error?.message || 'Failed to create team. Please try again.', 'error');
    },
  });

  const handleCreateTeam = () => {
    if (isTeamCreateValid) return;

    createTeamMutatation({
      name: teamName,
      county_id: selectedCounty,
      team_type_id: selectedTeamType!,
      dataset_ids: selectedDatasets,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
      <TextField
        type='text'
        label='Team name'
        variant='outlined'
        value={teamName}
        onChange={(e) => setTeamName(e.target.value)}
        sx={{ '& .MuiInputLabel-root': { fontSize: 14 } }}
      />
      <FormControl>
        <InputLabel id='select-county-label' sx={{ fontSize: '14px' }}>
          Select a county
        </InputLabel>
        <Select
          labelId='select-county-label'
          value={selectedCounty?.toString() || ''}
          label='Select a county'
          defaultValue={''}
          onChange={(e) => handleCountyChange(Number(e.target.value))}
        >
          {counties?.map((county) => (
            <MenuItem key={county.id} value={county.id}>
              {county.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <DatasetsMultiSelect
        counties={counties || []}
        value={selectedDatasets}
        lockedCountyId={selectedCounty}
        onChange={setSelectedDatasets}
        label='Select datasets'
      />
      <FormControl>
        <InputLabel id='select-team-type-label' sx={{ fontSize: '14px' }}>
          Select a team type
        </InputLabel>
        <Select
          labelId='select-team-type-label'
          value={selectedTeamType?.toString() || ''}
          label='Select a team type'
          defaultValue={''}
          onChange={(e) => setSelectedTeamType(Number(e.target.value))}
        >
          {teamTypes?.map((teamType) => (
            <MenuItem key={teamType.id} value={teamType.id}>
              {teamType.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant='contained'
        disableElevation
        disabled={isTeamCreateValid}
        onClick={() => {
          handleCreateTeam();
        }}
      >
        Create team
      </Button>
    </div>
  );
}

export default TeamCreateForm;
